import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { FileUploadDropzone } from "@/components/ui/file-upload-dropzone";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

interface Department {
  id: string;
  name: string;
}

interface TrainingVideoUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workProcessId?: string;
  onSuccess: () => void;
}

export function TrainingVideoUploadDialog({
  open,
  onOpenChange,
  workProcessId,
  onSuccess,
}: TrainingVideoUploadDialogProps) {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [isMandatory, setIsMandatory] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (open) {
      loadDepartments();
    }
  }, [open]);

  const loadDepartments = async () => {
    const { data } = await supabase
      .from('departments')
      .select('id, name')
      .order('sort_order');
    setDepartments(data || []);
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setCategory("");
    setIsMandatory(false);
    setSelectedDepartments([]);
    setVideoFile(null);
    setUploadProgress(0);
  };

  const generateThumbnail = (file: File): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      
      video.onloadeddata = () => {
        // Seek to 1 second or 10% of video, whichever is smaller
        video.currentTime = Math.min(1, video.duration * 0.1);
      };
      
      video.onseeked = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(video.src);
          resolve(blob);
        }, 'image/jpeg', 0.8);
      };
      
      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        resolve(null);
      };
      
      video.src = URL.createObjectURL(file);
    });
  };

  const handleSubmit = async () => {
    if (!title.trim() || !videoFile || !user) {
      toast.error("Bitte geben Sie einen Titel ein und wählen Sie ein Video");
      return;
    }

    setUploading(true);
    try {
      // Generate thumbnail from video
      const thumbnailBlob = await generateThumbnail(videoFile);
      let thumbnailPath: string | null = null;

      // Upload thumbnail if generated
      if (thumbnailBlob) {
        const thumbnailFileName = `thumbnails/${crypto.randomUUID()}.jpg`;
        const { error: thumbError } = await supabase.storage
          .from('training-videos')
          .upload(thumbnailFileName, thumbnailBlob, {
            cacheControl: '3600',
            contentType: 'image/jpeg',
          });

        if (!thumbError) {
          thumbnailPath = thumbnailFileName;
        }
      }

      // Upload video
      const fileExt = videoFile.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `videos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('training-videos')
        .upload(filePath, videoFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Create database record
      const { error: dbError } = await supabase.from('training_videos').insert({
        title: title.trim(),
        description: description.trim() || null,
        video_path: filePath,
        thumbnail_path: thumbnailPath,
        video_size: videoFile.size,
        category: category.trim() || null,
        is_mandatory: isMandatory,
        mandatory_for_departments: isMandatory ? selectedDepartments : [],
        work_process_id: workProcessId || null,
        created_by: user.id,
      });

      if (dbError) throw dbError;

      toast.success("Video erfolgreich hochgeladen!");
      resetForm();
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error("Fehler beim Hochladen des Videos");
    } finally {
      setUploading(false);
    }
  };

  const toggleDepartment = (deptId: string) => {
    setSelectedDepartments(prev =>
      prev.includes(deptId)
        ? prev.filter(id => id !== deptId)
        : [...prev, deptId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Lernvideo hochladen</DialogTitle>
          <DialogDescription>
            Laden Sie ein Schulungsvideo hoch, das Mitarbeiter ansehen können.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Titel *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Einführung Messgerät XY"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beschreibung</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optionale Beschreibung des Inhalts..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Kategorie</Label>
            <Input
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="z.B. Software, Messgeräte, Sicherheit"
            />
          </div>

          <div className="space-y-2">
            <Label>Video-Datei *</Label>
            <FileUploadDropzone
              accept="video/*"
              onFileSelect={setVideoFile}
              selectedFile={videoFile}
              onClear={() => setVideoFile(null)}
              title="Video auswählen"
              description="MP4, WebM oder MOV (max. 500MB)"
              acceptedFormats={["MP4", "WebM", "MOV"]}
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Switch
              id="mandatory"
              checked={isMandatory}
              onCheckedChange={setIsMandatory}
            />
            <Label htmlFor="mandatory" className="cursor-pointer">
              Pflicht-Video (muss von Mitarbeitern bestätigt werden)
            </Label>
          </div>

          {isMandatory && (
            <div className="space-y-2 p-3 bg-muted rounded-lg">
              <Label className="text-sm">Pflicht für Abteilungen:</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Leer lassen = für alle verpflichtend
              </p>
              <div className="grid grid-cols-2 gap-2">
                {departments.map(dept => (
                  <div key={dept.id} className="flex items-center gap-2">
                    <Checkbox
                      id={dept.id}
                      checked={selectedDepartments.includes(dept.id)}
                      onCheckedChange={() => toggleDepartment(dept.id)}
                    />
                    <Label htmlFor={dept.id} className="text-sm cursor-pointer">
                      {dept.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
              Abbrechen
            </Button>
            <Button onClick={handleSubmit} disabled={uploading || !videoFile || !title.trim()}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird hochgeladen...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Hochladen
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
